import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faSync,faChartLine,
  faEye,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faClock,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faCalendarAlt,
  faMoneyBillWave,
  faCreditCard,
  faFileInvoice,
  faUser,
  faBuilding,
  faEnvelope,
  faPhone,
  faCog,
  faSpinner,
  faTimes,
  faBan,
  faUndo,
  faReceipt
} from '@fortawesome/free-solid-svg-icons';
import '../styles/SubscriptionManagement.css';

const SubscriptionList = ({ embedded = false, onNavigate, setFormContent, onBreadcrumbUpdate }) => {
  // ==============================================
  // STATE MANAGEMENT
  // ==============================================
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plan_type: '',
    payment_status: '',
    application_id: '',
    expiring_soon: false,
    expiring_days: 30
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  
  const [formData, setFormData] = useState({
    root_user_id: '',
    application_id: '',
    plan_type: 'yearly',
    start_date: '',
    amount: '',
    status: 'active',
    auto_suspend: 1,
    grace_period_days: 7,
    payment_status: 'unpaid'
  });
  
  const [stats, setStats] = useState({
    total_customers: 0,
    active_subscriptions: 0,
    expiring_this_month: 0,
    expired_accounts: 0,
    outstanding_dues: 0,
    revenue_this_month: 0,
    revenue_this_year: 0
  });
  
  const [customers, setCustomers] = useState([]);
  const [applications, setApplications] = useState([]);
  
  const [viewSubscription, setViewSubscription] = useState(null);
  const [subscriptionTimeline, setSubscriptionTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  
  const [renewalData, setRenewalData] = useState({
    amount: '',
    notes: ''
  });

  // ==============================================
  // AUTH & USER CONTEXT
  // ==============================================
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_mechanicsap' || user?.role === 'admin_pos';

  // ==============================================
  // API HELPER FUNCTIONS
  // ==============================================
  const getAuthHeaders = () => {
    const authData = {
      user_id: user?.id || 1,
      username: user?.username || 'admin',
      role: user?.role || 'admin'
    };
    const authToken = btoa(JSON.stringify(authData));
    return {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
  };

  const apiRequest = async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...(options.headers || {})
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.message || 'API request failed');
      }
      
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  };

  // ==============================================
  // FETCH FUNCTIONS
  // ==============================================
  
  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        action: 'list',
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.plan_type) queryParams.append('plan_type', filters.plan_type);
      if (filters.payment_status) queryParams.append('payment_status', filters.payment_status);
      if (filters.application_id) queryParams.append('application_id', filters.application_id);
      if (filters.expiring_soon) {
        queryParams.append('expiring_soon', 'true');
        queryParams.append('expiring_days', filters.expiring_days);
      }
      
      const data = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_subscriptions.php?${queryParams.toString()}`
      );
      
      if (data.success) {
        setSubscriptions(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0
        }));
      } else {
        throw new Error(data.error || 'Failed to fetch subscriptions');
      }
    } catch (error) {
      setError(error.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php?action=get_stats'
      );
      
      if (data.success) {
        setStats(data.data || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_users.php?action=list&limit=1000`
      );
      
      if (data.success) {
        setCustomers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_applications.php?action=get_active'
      );
      
      if (data.success) {
        setApplications(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  }, []);

  const fetchSubscriptionTimeline = useCallback(async (subscriptionId) => {
    setLoadingTimeline(true);
    try {
      const data = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_manage_subscriptions.php?action=get_timeline&id=${subscriptionId}`
      );
      
      if (data.success) {
        setSubscriptionTimeline(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoadingTimeline(false);
    }
  }, []);

  // ==============================================
  // CRUD OPERATIONS
  // ==============================================

  const createSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'create',
            ...formData,
            created_by: user.id
          })
        }
      );
      
      if (data.success) {
        // Show invoice info if created
        if (data.invoice) {
          setSuccess(`Subscription created! Invoice #${data.invoice.invoice_no} generated for ${data.invoice.total_amount}`);
        } else {
          setSuccess('Subscription created successfully!');
        }
        setShowCreateModal(false);
        resetForm();
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to create subscription');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renewSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'renew',
            subscription_id: selectedSubscription?.id,
            performed_by: user.id,
            amount: renewalData.amount || selectedSubscription?.amount
          })
        }
      );
      
      if (data.success) {
        if (data.invoice) {
          setSuccess(`Subscription renewed! Invoice #${data.invoice.invoice_no} generated for ${data.invoice.total_amount}`);
        } else {
          setSuccess('Subscription renewed successfully!');
        }
        setShowRenewModal(false);
        setRenewalData({ amount: '', notes: '' });
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to renew subscription');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (subscriptionId, newStatus) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'update_status',
            subscription_id: subscriptionId,
            status: newStatus,
            performed_by: user.id
          })
        }
      );
      
      if (data.success) {
        setSuccess(`Status updated to ${newStatus}`);
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentStatus = async (subscriptionId, paymentStatus) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'POST',
          body: JSON.stringify({
            action: 'update_payment',
            subscription_id: subscriptionId,
            payment_status: paymentStatus
          })
        }
      );
      
      if (data.success) {
        setSuccess(`Payment status updated to ${paymentStatus}`);
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to update payment status');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteSubscription = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'DELETE',
          body: JSON.stringify({
            subscription_id: selectedSubscription?.id,
            performed_by: user.id
          })
        }
      );
      
      if (data.success) {
        setSuccess('Subscription deleted successfully');
        setShowDeleteModal(false);
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to delete subscription');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update subscription
  const updateSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiRequest(
        'https://ourzestapp.online/oz_backend/root_subscriptions.php',
        {
          method: 'PUT',
          body: JSON.stringify({
            action: 'update_subscription',
            subscription_id: selectedSubscription?.id,
            performed_by: user.id,
            root_user_id: formData.root_user_id,
            application_id: formData.application_id,
            plan_type: formData.plan_type,
            start_date: formData.start_date,
            amount: formData.amount,
            status: formData.status,
            auto_suspend: formData.auto_suspend,
            grace_period_days: formData.grace_period_days,
            payment_status: formData.payment_status
          })
        }
      );
      
      if (data.success) {
        setSuccess('Subscription updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchSubscriptions();
        fetchStats();
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to update subscription');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================
  // HELPER FUNCTIONS
  // ==============================================

  const resetForm = () => {
    setFormData({
      root_user_id: '',
      application_id: '',
      plan_type: 'yearly',
      start_date: '',
      amount: '',
      status: 'active',
      auto_suspend: 1,
      grace_period_days: 7,
      payment_status: 'unpaid'
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleRenewalChange = (e) => {
    const { name, value } = e.target;
    setRenewalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      plan_type: '',
      payment_status: '',
      application_id: '',
      expiring_soon: false,
      expiring_days: 30
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#10b981',
      'grace_period': '#f59e0b',
      'expired': '#ef4444',
      'cancelled': '#6b7280',
      'pending': '#3b82f6',
      'suspended': '#dc2626'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Active',
      'grace_period': 'Grace Period',
      'expired': 'Expired',
      'cancelled': 'Cancelled',
      'pending': 'Pending',
      'suspended': 'Suspended'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': '#10b981',
      'unpaid': '#ef4444',
      'partially_paid': '#f59e0b',
      'overdue': '#dc2626'
    };
    return colors[status] || '#6b7280';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'paid': 'Paid',
      'unpaid': 'Unpaid',
      'partially_paid': 'Partially Paid',
      'overdue': 'Overdue'
    };
    return labels[status] || status;
  };

  const getDaysRemainingColor = (days) => {
    if (days < 0) return '#ef4444';
    if (days <= 3) return '#ef4444';
    if (days <= 7) return '#f59e0b';
    if (days <= 15) return '#fbbf24';
    if (days <= 30) return '#3b82f6';
    return '#10b981';
  };

  const getTimelineIcon = (type) => {
    const icons = {
      'subscription_created': faRocket,
      'renewal': faSync,
      'payment': faCreditCard,
      'status_change': faCog,
      'suspension': faBan,
      'reactivation': faUndo,
      'invoice_generated': faFileInvoice,
      'receipt_created': faReceipt
    };
    return icons[type] || faClock;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getPlanTypeLabel = (type) => {
    const labels = {
      'monthly': 'Monthly',
      'yearly': 'Yearly',
      'custom': 'Custom'
    };
    return labels[type] || type;
  };

  // ==============================================
  // EFFECTS
  // ==============================================

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
    fetchCustomers();
    fetchApplications();
    
    if (onBreadcrumbUpdate) {
      onBreadcrumbUpdate('billing', 'managebillings');
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [pagination.page, filters]);

  // ==============================================
  // MODAL HANDLERS
  // ==============================================

  const handleViewSubscription = async (id) => {
    try {
      // Get subscription details with invoice info
      const subData = await apiRequest(
        `https://ourzestapp.online/oz_backend/root_subscriptions.php?action=get&id=${id}`
      );
      
      if (subData.success) {
        setViewSubscription(subData.data);
        await fetchSubscriptionTimeline(id);
        
        // Fetch invoice for this subscription
        try {
          const invoiceData = await apiRequest(
            `https://ourzestapp.online/oz_backend/root_invoices.php?action=get_invoices&subscription_id=${id}&limit=1`
          );
          if (invoiceData.success && invoiceData.data.length > 0) {
            setInvoiceInfo(invoiceData.data[0]);
          } else {
            setInvoiceInfo(null);
          }
        } catch (e) {
          setInvoiceInfo(null);
        }
        
        setShowViewModal(true);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRenewSubscription = (sub) => {
    setSelectedSubscription(sub);
    setRenewalData({
      amount: sub.amount || '',
      notes: ''
    });
    setShowRenewModal(true);
  };

  const handleEditSubscription = (sub) => {
    setSelectedSubscription(sub);
    setFormData({
      root_user_id: sub.root_user_id || '',
      application_id: sub.application_id || '',
      plan_type: sub.plan_type || 'yearly',
      start_date: sub.start_date || '',
      amount: sub.amount || '',
      status: sub.status || 'active',
      auto_suspend: sub.auto_suspend || 1,
      grace_period_days: sub.grace_period_days || 7,
      payment_status: sub.payment_status || 'unpaid'
    });
    setShowEditModal(true);
  };

  const handleDeleteSubscription = (sub) => {
    setSelectedSubscription(sub);
    setShowDeleteModal(true);
  };

  // ==============================================
  // RENDER FUNCTIONS
  // ==============================================

  const renderStats = () => (
    <div className="subscription-stats-grid">
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#e0f7fa' }}>
          <FontAwesomeIcon icon={faUser} style={{ color: '#0288d1' }} />
        </div>
        <div className="stat-info">
          <h3>{stats.total_customers || 0}</h3>
          <p>Total Customers</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#388e3c' }} />
        </div>
        <div className="stat-info">
          <h3>{stats.active_subscriptions || 0}</h3>
          <p>Active Subscriptions</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#fff3e0' }}>
          <FontAwesomeIcon icon={faClock} style={{ color: '#f57c00' }} />
        </div>
        <div className="stat-info">
          <h3>{stats.expiring_this_month || 0}</h3>
          <p>Expiring This Month</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#ffebee' }}>
          <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#c62828' }} />
        </div>
        <div className="stat-info">
          <h3>{stats.expired_accounts || 0}</h3>
          <p>Expired Accounts</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#fce4ec' }}>
          <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#c62828' }} />
        </div>
        <div className="stat-info">
          <h3>{formatCurrency(stats.outstanding_dues || 0)}</h3>
          <p>Outstanding Dues</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: '#e8f5e9' }}>
          <FontAwesomeIcon icon={faChartLine} style={{ color: '#2e7d32' }} />
        </div>
        <div className="stat-info">
          <h3>{formatCurrency(stats.revenue_this_month || 0)}</h3>
          <p>Revenue This Month</p>
        </div>
      </div>
    </div>
  );

  const renderFilters = () => (
    <div className="subscription-filters">
      <div className="filter-search">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          name="search"
          placeholder="Search customers, applications..."
          value={filters.search}
          onChange={handleFilterChange}
          className="filter-input"
        />
      </div>
      
      <div className="filter-group">
        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="grace_period">Grace Period</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="suspended">Suspended</option>
        </select>
        
        <select name="plan_type" value={filters.plan_type} onChange={handleFilterChange} className="filter-select">
          <option value="">All Plans</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="custom">Custom</option>
        </select>
        
        <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange} className="filter-select">
          <option value="">All Payments</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="overdue">Overdue</option>
        </select>
        
        <select name="application_id" value={filters.application_id} onChange={handleFilterChange} className="filter-select">
          <option value="">All Applications</option>
          {applications.map(app => (
            <option key={app.id} value={app.id}>{app.app_name}</option>
          ))}
        </select>
      </div>
      
      <div className="filter-actions">
        <label className="filter-checkbox">
          <input type="checkbox" name="expiring_soon" checked={filters.expiring_soon} onChange={handleFilterChange} />
          <span>Expiring Soon</span>
        </label>
        <button className="btn btn-clear" onClick={clearFilters}>Clear Filters</button>
      </div>
    </div>
  );

  const renderTable = () => (
    <div className="subscription-table-container">
      <table className="subscription-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Application</th>
            <th>Plan</th>
            <th>Start Date</th>
            <th>Expiry Date</th>
            <th>Days Left</th>
            <th>Status</th>
            <th>Payment</th>
            <th>Amount</th>
            <th>Invoice</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="11" className="loading-cell">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading subscriptions...</span>
              </td>
            </tr>
          ) : subscriptions.length === 0 ? (
            <tr>
              <td colSpan="11" className="empty-cell">
                <p>No subscriptions found</p>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Create Subscription
                </button>
              </td>
            </tr>
          ) : (
            subscriptions.map(sub => {
              const daysRemaining = sub.days_remaining || 0;
              const isExpiringSoon = daysRemaining <= 7 && daysRemaining > 0;
              
              return (
                <tr key={sub.id} className={isExpiringSoon ? 'expiring-soon' : ''}>
                  <td>
                    <div className="customer-info">
                      <div className="customer-avatar">
                        {sub.customer_fullname?.charAt(0) || sub.customer_username?.charAt(0) || 'U'}
                      </div>
                      <div className="customer-details">
                        <div className="customer-name">{sub.customer_fullname || sub.customer_username}</div>
                        <div className="customer-business">{sub.customer_business || 'Individual'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="app-info">
                      <div className="app-name">{sub.app_name}</div>
                      <div className="app-code">{sub.app_code}</div>
                    </div>
                  </td>
                  <td>
                    <span className="plan-badge">{getPlanTypeLabel(sub.plan_type)}</span>
                  </td>
                  <td>{formatDate(sub.start_date)}</td>
                  <td>{formatDate(sub.expiry_date)}</td>
                  <td>
                    <span className="days-badge" style={{ backgroundColor: getDaysRemainingColor(daysRemaining) }}>
                      {daysRemaining < 0 ? 'Expired' : `${daysRemaining} days`}
                    </span>
                  </td>
                  <td>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(sub.status) }}>
                      <FontAwesomeIcon 
                        icon={sub.status === 'active' ? faCheckCircle : sub.status === 'grace_period' ? faClock : sub.status === 'expired' ? faTimesCircle : sub.status === 'suspended' ? faBan : faCircle} 
                        className="status-icon"
                      />
                      {getStatusLabel(sub.status)}
                    </span>
                  </td>
                  <td>
                    <span className="payment-badge" style={{ backgroundColor: getPaymentStatusColor(sub.payment_status) }}>
                      {getPaymentStatusLabel(sub.payment_status)}
                    </span>
                  </td>
                  <td className="amount-cell">{formatCurrency(sub.amount)}</td>
                  <td>
                    <button 
                      className="action-btn invoice-btn"
                      onClick={() => {
                        // Navigate to invoices with filter by subscription
                        if (onNavigate) {
                          onNavigate('billing', 'invoices');
                        }
                      }}
                      title="View Invoice"
                    >
                      <FontAwesomeIcon icon={faFileInvoice} />
                    </button>
                  </td>
                  <td className="actions-cell">
                    <button className="action-btn view-btn" onClick={() => handleViewSubscription(sub.id)} title="View Details">
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button 
                      className="action-btn renew-btn" 
                      onClick={() => handleRenewSubscription(sub)} 
                      title="Renew"
                      disabled={sub.status === 'suspended' || sub.status === 'cancelled'}
                    >
                      <FontAwesomeIcon icon={faSync} />
                    </button>
                    {isAdmin && (
                      <>
                        <button className="action-btn edit-btn" onClick={() => handleEditSubscription(sub)} title="Edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="action-btn delete-btn" onClick={() => handleDeleteSubscription(sub)} title="Delete">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const renderPagination = () => (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {subscriptions.length} of {pagination.total} subscriptions
      </div>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          disabled={pagination.page <= 1}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className="page-info">Page {pagination.page} of {pagination.pages || 1}</span>
        <button
          className="pagination-btn"
          onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          disabled={pagination.page >= pagination.pages}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );

  // ==============================================
  // MODAL RENDERERS
  // ==============================================

  const renderFormModal = (isEdit = false) => (
    <div className="modal-overlay" onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
      <div className="modal-content subscription-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Subscription' : 'Create New Subscription'}</h3>
          <button className="modal-close" onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <form onSubmit={isEdit ? updateSubscription : createSubscription}>
          <div className="form-row">
            <div className="form-group">
              <label>Customer *</label>
              <select name="root_user_id" value={formData.root_user_id} onChange={handleInputChange} required>
                <option value="">Select Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullname || customer.username} - {customer.businessname || 'Individual'}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Application *</label>
              <select name="application_id" value={formData.application_id} onChange={handleInputChange} required>
                <option value="">Select Application</option>
                {applications.map(app => (
                  <option key={app.id} value={app.id}>
                    {app.app_name} {app.app_code ? `(${app.app_code})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Plan Type *</label>
              <select name="plan_type" value={formData.plan_type} onChange={handleInputChange} required>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Start Date *</label>
              <input type="date" name="start_date" value={formData.start_date} onChange={handleInputChange} required />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Amount</label>
              <input type="number" name="amount" value={formData.amount} onChange={handleInputChange} placeholder="0.00" step="0.01" />
              <small>Leave empty to use default pricing</small>
            </div>
            
            <div className="form-group">
              <label>Payment Status</label>
              <select name="payment_status" value={formData.payment_status} onChange={handleInputChange}>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleInputChange}>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Grace Period (Days)</label>
              <input type="number" name="grace_period_days" value={formData.grace_period_days} onChange={handleInputChange} min="0" max="30" />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" name="auto_suspend" checked={formData.auto_suspend === 1} onChange={handleInputChange} />
                <span>Auto-suspend when expired</span>
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
              {isEdit ? 'Update Subscription' : 'Create Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderRenewModal = () => (
    <div className="modal-overlay" onClick={() => setShowRenewModal(false)}>
      <div className="modal-content renewal-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Renew Subscription</h3>
          <button className="modal-close" onClick={() => setShowRenewModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {selectedSubscription && (
          <div className="subscription-summary">
            <div className="summary-item">
              <span className="label">Customer:</span>
              <span className="value">{selectedSubscription.customer_fullname || selectedSubscription.customer_username}</span>
            </div>
            <div className="summary-item">
              <span className="label">Application:</span>
              <span className="value">{selectedSubscription.app_name}</span>
            </div>
            <div className="summary-item">
              <span className="label">Current Expiry:</span>
              <span className="value">{formatDate(selectedSubscription.expiry_date)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Days Remaining:</span>
              <span className="value" style={{ color: getDaysRemainingColor(selectedSubscription.days_remaining || 0) }}>
                {selectedSubscription.days_remaining || 0} days
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Amount:</span>
              <span className="value">{formatCurrency(selectedSubscription.amount)}</span>
            </div>
          </div>
        )}
        
        <form onSubmit={renewSubscription}>
          <div className="form-row">
            <div className="form-group">
              <label>Renewal Amount</label>
              <input type="number" name="amount" value={renewalData.amount} onChange={handleRenewalChange} placeholder="Enter amount" step="0.01" required />
              <small>Leave as is to use current amount</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={renewalData.notes} onChange={handleRenewalChange} placeholder="Optional notes about this renewal" rows="2" />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowRenewModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
              <FontAwesomeIcon icon={faSync} /> Renew Subscription
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderViewModal = () => (
    <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
      <div className="modal-content view-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Subscription Details</h3>
          <button className="modal-close" onClick={() => setShowViewModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {viewSubscription && (
          <div className="view-content">
            <div className="view-section customer-section">
              <h4><FontAwesomeIcon icon={faUser} /> Customer Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Name:</span>
                  <span className="value">{viewSubscription.customer_fullname || viewSubscription.customer_username}</span>
                </div>
                <div className="info-item">
                  <span className="label">Business:</span>
                  <span className="value">{viewSubscription.customer_business || 'Individual'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Email:</span>
                  <span className="value">{viewSubscription.customer_email || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Phone:</span>
                  <span className="value">{viewSubscription.customer_phone || 'N/A'}</span>
                </div>
              </div>
            </div>
            
            <div className="view-section subscription-section">
              <h4><FontAwesomeIcon icon={faFileInvoice} /> Subscription Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">Application:</span>
                  <span className="value">{viewSubscription.app_name}</span>
                </div>
                <div className="info-item">
                  <span className="label">Plan:</span>
                  <span className="value">{getPlanTypeLabel(viewSubscription.plan_type)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Amount:</span>
                  <span className="value">{formatCurrency(viewSubscription.amount)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(viewSubscription.status) }}>
                    {getStatusLabel(viewSubscription.status)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Payment Status:</span>
                  <span className="payment-badge" style={{ backgroundColor: getPaymentStatusColor(viewSubscription.payment_status) }}>
                    {getPaymentStatusLabel(viewSubscription.payment_status)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Days Remaining:</span>
                  <span className="value" style={{ color: getDaysRemainingColor(viewSubscription.days_remaining || 0) }}>
                    {viewSubscription.days_remaining || 0} days
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Start Date:</span>
                  <span className="value">{formatDate(viewSubscription.start_date)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Expiry Date:</span>
                  <span className="value">{formatDate(viewSubscription.expiry_date)}</span>
                </div>
              </div>
            </div>

            {/* Invoice Info */}
            {invoiceInfo && (
              <div className="view-section invoice-section">
                <h4><FontAwesomeIcon icon={faFileInvoice} /> Invoice Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Invoice #:</span>
                    <span className="value">{invoiceInfo.invoice_no}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Total Amount:</span>
                    <span className="value">{formatCurrency(invoiceInfo.total_amount)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Paid Amount:</span>
                    <span className="value">{formatCurrency(invoiceInfo.paid_amount)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Due Amount:</span>
                    <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(invoiceInfo.due_amount)}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Invoice Status:</span>
                    <span className="status-badge" style={{ backgroundColor: getStatusColor(invoiceInfo.status) }}>
                      {getStatusLabel(invoiceInfo.status)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="view-section timeline-section">
              <h4><FontAwesomeIcon icon={faHistory} /> Timeline</h4>
              {loadingTimeline ? (
                <div className="loading-timeline">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Loading timeline...</span>
                </div>
              ) : subscriptionTimeline.length === 0 ? (
                <p className="empty-timeline">No events recorded</p>
              ) : (
                <div className="timeline">
                  {subscriptionTimeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-icon">
                        <FontAwesomeIcon icon={getTimelineIcon(event.type)} />
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-title">{event.title}</span>
                          <span className="timeline-date">{event.date_formatted || formatDate(event.date)}</span>
                        </div>
                        {event.description && (
                          <p className="timeline-description">{event.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="view-actions">
              <button 
                className="btn btn-success"
                onClick={() => {
                  setShowViewModal(false);
                  handleRenewSubscription(viewSubscription);
                }}
                disabled={viewSubscription.status === 'suspended' || viewSubscription.status === 'cancelled'}
              >
                <FontAwesomeIcon icon={faSync} /> Renew
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowViewModal(false);
                  handleEditSubscription(viewSubscription);
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
              {invoiceInfo && invoiceInfo.status !== 'paid' && (
                <button 
                  className="btn btn-info"
                  onClick={() => {
                    setShowViewModal(false);
                    if (onNavigate) {
                      onNavigate('billing', 'invoices');
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCreditCard} /> Pay Invoice
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDeleteModal = () => (
    <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
      <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Confirm Delete</h3>
          <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="delete-content">
          <FontAwesomeIcon icon={faExclamationTriangle} className="delete-icon" />
          <p>Are you sure you want to delete this subscription?</p>
          {selectedSubscription && (
            <div className="delete-details">
              <div><strong>Customer:</strong> {selectedSubscription.customer_fullname || selectedSubscription.customer_username}</div>
              <div><strong>Application:</strong> {selectedSubscription.app_name}</div>
              <div><strong>Expiry:</strong> {formatDate(selectedSubscription.expiry_date)}</div>
            </div>
          )}
          <p className="delete-warning">This action cannot be undone.</p>
        </div>
        
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
          <button className="btn btn-danger" onClick={deleteSubscription} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
            Delete Subscription
          </button>
        </div>
      </div>
    </div>
  );

  // ==============================================
  // MAIN RENDER
  // ==============================================

  return (
    <div className={`subscription-management ${embedded ? 'embedded' : ''}`}>
      <div className="subscription-header">
        <div className="header-left">
          <h2>Subscriptions</h2>
          <span className="subscription-count">{pagination.total} total</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-refresh" onClick={() => { fetchSubscriptions(); fetchStats(); }}>
            <FontAwesomeIcon icon={faSync} /> Refresh
          </button>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> New Subscription
            </button>
          )}
        </div>
      </div>
      
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      
      {renderStats()}
      {renderFilters()}
      {renderTable()}
      {renderPagination()}
      
      {showCreateModal && renderFormModal(false)}
      {showEditModal && renderFormModal(true)}
      {showViewModal && renderViewModal()}
      {showRenewModal && renderRenewModal()}
      {showDeleteModal && renderDeleteModal()}
    </div>
  );
};

export default SubscriptionList;